import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Pencil, Trash2, Plus, Shield, Flame, Search, Globe,
  Settings, Factory, X, LayoutGrid, List as ListIcon, Camera,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ALL_SYSTEM_TYPES, getSystemCategory } from "@/lib/systemTypeConstants";
import { getSystemTypeIcon } from "@/lib/systemTypeIcons";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

type Row = { id: string; nombre: string; sistema_tipo: string; empresa_id: string | null; marca_id?: string | null; tipo_equipo?: string | null; categoria?: string };
type EditState = { id: string | null; value: string };

const getSystemIcon = getSystemTypeIcon;

const normalizar = (txt: string) =>
  (txt || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();

export default function CatalogoGlobal() {
  const { toast } = useToast();
  const [tipos, setTipos] = useState<Row[]>([]);
  const [marcas, setMarcas] = useState<Row[]>([]);
  const [modelos, setModelos] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const [activeSystemType, setActiveSystemType] = useState("CCTV");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<'todos' | 'seguridad' | 'pci'>('todos');
  const [activeTipo, setActiveTipo] = useState<string | null>(null);
  const [activeMarca, setActiveMarca] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'lista'>('lista');

  const [editTipo, setEditTipo] = useState<EditState>({ id: null, value: "" });
  const [editMarca, setEditMarca] = useState<EditState>({ id: null, value: "" });
  const [editModelo, setEditModelo] = useState<EditState>({ id: null, value: "" });

  const [nuevoTipo, setNuevoTipo] = useState("");
  const [nuevaMarca, setNuevaMarca] = useState("");
  const [nuevoModelo, setNuevoModelo] = useState("");
  const [marcaParaNuevoModelo, setMarcaParaNuevoModelo] = useState("");

  const systemTypes = Object.entries(ALL_SYSTEM_TYPES).map(([tipo, nombre]) => ({
    tipo, nombre: nombre as string, categoria: getSystemCategory(tipo) as 'seguridad' | 'pci',
  }));
  const filteredSystemTypes = selectedCategory === 'todos'
    ? systemTypes
    : systemTypes.filter(s => s.categoria === selectedCategory);

  const cargarDatos = async () => {
    setLoading(true);
    const [t, m, mo] = await Promise.all([
      supabase.from('tipos_equipo').select('*').is('empresa_id', null),
      supabase.from('marcas_equipo').select('*').is('empresa_id', null),
      supabase.from('modelos_equipo').select('*').is('empresa_id', null),
    ]);
    if (t.data) setTipos(t.data as any);
    if (m.data) setMarcas(m.data as any);
    if (mo.data) setModelos(mo.data as any);
    setLoading(false);
  };

  useEffect(() => { cargarDatos(); }, []);

  const sistemaActivoNorm = normalizar(activeSystemType);
  const matchesSearch = (s?: string) =>
    !searchQuery || normalizar(s || '').includes(normalizar(searchQuery));

  const tiposDelSistema = useMemo(() =>
    tipos.filter(t => normalizar(t.sistema_tipo) === sistemaActivoNorm)
      .filter(t => matchesSearch(t.nombre))
      .sort((a, b) => a.nombre.localeCompare(b.nombre)),
    [tipos, sistemaActivoNorm, searchQuery]);

  const marcasDelSistema = useMemo(() =>
    marcas.filter(m => normalizar(m.sistema_tipo) === sistemaActivoNorm)
      .filter(m => matchesSearch(m.nombre))
      .sort((a, b) => a.nombre.localeCompare(b.nombre)),
    [marcas, sistemaActivoNorm, searchQuery]);

  const modelosDelSistema = useMemo(() => {
    const tipoObj = tipos.find(t => t.id === activeTipo);
    const tipoNorm = tipoObj ? normalizar(tipoObj.nombre) : null;
    return modelos
      .filter(m => normalizar(m.sistema_tipo) === sistemaActivoNorm)
      .filter(m => !activeMarca || m.marca_id === activeMarca)
      .filter(m => !tipoNorm || (m.tipo_equipo && normalizar(m.tipo_equipo) === tipoNorm))
      .filter(m => matchesSearch(m.nombre))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [modelos, tipos, sistemaActivoNorm, activeTipo, activeMarca, searchQuery]);

  const getMarcaById = (id?: string | null) => marcas.find(m => m.id === id);
  const getCategoria = () => systemTypes.find(s => s.tipo === activeSystemType)?.categoria || 'seguridad';

  // ============ CRUD ============
  const handleCrearTipo = async () => {
    if (!nuevoTipo.trim()) return;
    const { error } = await supabase.from('tipos_equipo').insert({
      nombre: nuevoTipo.trim(),
      sistema_tipo: sistemaActivoNorm,
      categoria: getCategoria(),
      empresa_id: null,
    });
    if (error) return toast({ title: "Error", description: error.message, variant: "destructive" });
    setNuevoTipo("");
    cargarDatos();
    toast({ title: "Tipo global creado" });
  };

  const handleCrearMarca = async () => {
    if (!nuevaMarca.trim()) return;
    const { error } = await supabase.from('marcas_equipo').insert({
      nombre: nuevaMarca.trim(),
      sistema_tipo: sistemaActivoNorm,
      categoria: getCategoria(),
      empresa_id: null,
    });
    if (error) return toast({ title: "Error", description: error.message, variant: "destructive" });
    setNuevaMarca("");
    cargarDatos();
    toast({ title: "Marca global creada" });
  };

  const handleCrearModelo = async () => {
    const marcaId = marcaParaNuevoModelo || activeMarca;
    if (!nuevoModelo.trim() || !marcaId) {
      return toast({ title: "Falta marca", description: "Selecciona una marca.", variant: "destructive" });
    }
    const tipoObj = tipos.find(t => t.id === activeTipo);
    const { error } = await supabase.from('modelos_equipo').insert({
      nombre: nuevoModelo.trim(),
      marca_id: marcaId,
      sistema_tipo: sistemaActivoNorm,
      tipo_equipo: tipoObj?.nombre || null,
      categoria: getCategoria(),
      empresa_id: null,
    });
    if (error) return toast({ title: "Error", description: error.message, variant: "destructive" });
    setNuevoModelo("");
    setMarcaParaNuevoModelo("");
    cargarDatos();
    toast({ title: "Modelo global creado" });
  };

  const editarFila = async (tabla: 'tipos_equipo' | 'marcas_equipo' | 'modelos_equipo', id: string, nombre: string) => {
    const { error } = await supabase.from(tabla).update({ nombre }).eq('id', id);
    if (error) return toast({ title: "Error", description: error.message, variant: "destructive" });
    cargarDatos();
    toast({ title: "Actualizado" });
  };

  const eliminarFila = async (tabla: 'tipos_equipo' | 'marcas_equipo' | 'modelos_equipo', id: string, nombre: string) => {
    if (!confirm(`¿Eliminar "${nombre}" del catálogo global? Esta acción es irreversible.`)) return;
    const { error } = await supabase.from(tabla).delete().eq('id', id);
    if (error) return toast({ title: "Error", description: error.message, variant: "destructive" });
    cargarDatos();
    toast({ title: "Eliminado" });
  };

  // ============ Render helpers ============
  const renderPill = (
    item: Row,
    isActive: boolean,
    onToggle: () => void,
    editState: EditState,
    setEditState: (s: EditState) => void,
    tabla: 'tipos_equipo' | 'marcas_equipo',
  ) => {
    if (editState.id === item.id) {
      return (
        <div key={item.id} className="flex items-center gap-1 bg-background border border-primary rounded-full pl-3 pr-1 py-1">
          <Input
            value={editState.value}
            onChange={(e) => setEditState({ ...editState, value: e.target.value })}
            className="h-7 w-32 text-xs border-0 focus-visible:ring-0"
            autoFocus
            onKeyDown={async (e) => {
              if (e.key === 'Enter') {
                await editarFila(tabla, item.id, editState.value);
                setEditState({ id: null, value: "" });
              } else if (e.key === 'Escape') setEditState({ id: null, value: "" });
            }}
          />
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={async () => {
            await editarFila(tabla, item.id, editState.value);
            setEditState({ id: null, value: "" });
          }}>✓</Button>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditState({ id: null, value: "" })}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      );
    }
    return (
      <div
        key={item.id}
        className={cn(
          "group inline-flex items-center gap-1 rounded-full border pl-3 pr-1 py-1 text-sm transition-colors cursor-pointer",
          isActive ? "bg-primary text-primary-foreground border-primary" : "bg-muted/40 hover:bg-muted border-border"
        )}
        onClick={onToggle}
      >
        <span className="font-medium select-none">{item.nombre}</span>
        <div className="ml-1 flex items-center opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditState({ id: item.id, value: item.nombre })}>
            <Pencil className="h-3 w-3" />
          </Button>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive" onClick={() => eliminarFila(tabla, item.id, item.nombre)}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    );
  };

  const SystemIcon = getSystemIcon(activeSystemType);
  const activeTipoNombre = tipos.find(t => t.id === activeTipo)?.nombre;
  const activeMarcaNombre = getMarcaById(activeMarca)?.nombre;

  return (
    <div className="container mx-auto p-4 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Globe className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Catálogo Global</h1>
            <p className="text-muted-foreground text-sm">Tipos, marcas y modelos compartidos con todas las empresas</p>
          </div>
        </div>
        <div className="inline-flex rounded-md border bg-background p-0.5">
          <Button variant={viewMode === 'cards' ? 'default' : 'ghost'} size="sm" className="h-7 px-2" onClick={() => setViewMode('cards')}>
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button variant={viewMode === 'lista' ? 'default' : 'ghost'} size="sm" className="h-7 px-2" onClick={() => setViewMode('lista')}>
            <ListIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm text-amber-900 dark:text-amber-100">
        ⚠️ Los cambios aquí afectan a <strong>todas las empresas</strong>. Los recursos creados son visibles globalmente.
      </div>

      <div className="flex flex-col sm:flex-row gap-4 py-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
        </div>
        <div className="flex gap-2">
          <Button variant={selectedCategory === 'todos' ? 'default' : 'outline'} onClick={() => setSelectedCategory('todos')} size="sm">Todos</Button>
          <Button variant={selectedCategory === 'seguridad' ? 'default' : 'outline'} onClick={() => setSelectedCategory('seguridad')} size="sm" className="gap-1">
            <Shield className="h-4 w-4" /> Seguridad
          </Button>
          <Button variant={selectedCategory === 'pci' ? 'default' : 'outline'} onClick={() => setSelectedCategory('pci')} size="sm" className="gap-1">
            <Flame className="h-4 w-4" /> PCI
          </Button>
        </div>
      </div>

      <Tabs value={activeSystemType} onValueChange={(v) => { setActiveSystemType(v); setActiveTipo(null); setActiveMarca(null); }}>
        <TabsList className="mb-4 flex-wrap h-auto p-1">
          {filteredSystemTypes.map((st) => {
            const Icon = getSystemIcon(st.tipo);
            return (
              <TabsTrigger key={st.tipo} value={st.tipo} className="flex items-center gap-2">
                <Icon className="h-4 w-4" />{st.nombre}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {filteredSystemTypes.map((st) => (
          <TabsContent key={st.tipo} value={st.tipo} className="mt-0 space-y-4">
            <div className="flex items-center gap-3 p-4 rounded-lg border bg-muted/30">
              <SystemIcon className="h-6 w-6 text-primary" />
              <div className="flex-1">
                <h2 className="text-xl font-semibold">{st.nombre}</h2>
                <p className="text-xs text-muted-foreground">{tiposDelSistema.length} tipos · {marcasDelSistema.length} marcas · {modelosDelSistema.length} modelos</p>
              </div>
            </div>

            {/* TIPOS */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Settings className="h-4 w-4 text-primary" /> Tipos
                  <Badge variant="secondary">{tiposDelSistema.length}</Badge>
                  {activeTipo && (
                    <Button variant="ghost" size="sm" className="ml-auto h-7 text-xs" onClick={() => setActiveTipo(null)}>
                      <X className="h-3 w-3 mr-1" /> Quitar filtro
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {tiposDelSistema.map(tipo =>
                    renderPill(tipo, activeTipo === tipo.id,
                      () => setActiveTipo(activeTipo === tipo.id ? null : tipo.id),
                      editTipo, setEditTipo, 'tipos_equipo')
                  )}
                  {tiposDelSistema.length === 0 && <div className="text-sm text-muted-foreground py-2">Sin tipos globales.</div>}
                </div>
                <div className="flex gap-2 pt-2 border-t">
                  <Input placeholder="Nuevo tipo global..." value={nuevoTipo} onChange={(e) => setNuevoTipo(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCrearTipo()} className="max-w-xs h-9" />
                  <Button size="sm" onClick={handleCrearTipo} className="gap-1"><Plus className="h-4 w-4" /> Añadir</Button>
                </div>
              </CardContent>
            </Card>

            {/* MARCAS */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Factory className="h-4 w-4 text-primary" /> Marcas
                  <Badge variant="secondary">{marcasDelSistema.length}</Badge>
                  {activeMarca && (
                    <Button variant="ghost" size="sm" className="ml-auto h-7 text-xs" onClick={() => setActiveMarca(null)}>
                      <X className="h-3 w-3 mr-1" /> Quitar filtro
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {marcasDelSistema.map(marca =>
                    renderPill(marca, activeMarca === marca.id,
                      () => setActiveMarca(activeMarca === marca.id ? null : marca.id),
                      editMarca, setEditMarca, 'marcas_equipo')
                  )}
                  {marcasDelSistema.length === 0 && <div className="text-sm text-muted-foreground py-2">Sin marcas globales.</div>}
                </div>
                <div className="flex gap-2 pt-2 border-t">
                  <Input placeholder="Nueva marca global..." value={nuevaMarca} onChange={(e) => setNuevaMarca(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCrearMarca()} className="max-w-xs h-9" />
                  <Button size="sm" onClick={handleCrearMarca} className="gap-1"><Plus className="h-4 w-4" /> Añadir</Button>
                </div>
              </CardContent>
            </Card>

            {/* MODELOS */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                  <Camera className="h-4 w-4 text-primary" /> Modelos
                  <Badge variant="secondary">{modelosDelSistema.length}</Badge>
                  {(activeTipo || activeMarca) && (
                    <div className="flex items-center gap-2 ml-2 text-xs font-normal text-muted-foreground">
                      Filtrando por:
                      {activeTipoNombre && <Badge variant="outline">Tipo: {activeTipoNombre}</Badge>}
                      {activeMarcaNombre && <Badge variant="outline">Marca: {activeMarcaNombre}</Badge>}
                    </div>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {viewMode === 'lista' ? (
                  <div className="border rounded-md max-h-[520px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 sticky top-0 z-10">
                        <tr className="text-left text-xs text-muted-foreground">
                          <th className="px-3 py-2 font-medium">Modelo</th>
                          <th className="px-3 py-2 font-medium">Marca</th>
                          <th className="px-3 py-2 font-medium">Tipo</th>
                          <th className="px-3 py-2 font-medium w-px text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modelosDelSistema.map((modelo) => {
                          const marca = getMarcaById(modelo.marca_id);
                          const editing = editModelo.id === modelo.id;
                          return (
                            <tr key={modelo.id} className="border-t hover:bg-muted/40">
                              <td className="px-3 py-2 font-medium">
                                {editing ? (
                                  <div className="flex items-center gap-1">
                                    <Input value={editModelo.value} onChange={(e) => setEditModelo({ ...editModelo, value: e.target.value })} className="h-7 text-sm" autoFocus />
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={async () => {
                                      await editarFila('modelos_equipo', modelo.id, editModelo.value);
                                      setEditModelo({ id: null, value: "" });
                                    }}>✓</Button>
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditModelo({ id: null, value: "" })}>
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ) : modelo.nombre}
                              </td>
                              <td className="px-3 py-2 text-muted-foreground">{marca?.nombre || '—'}</td>
                              <td className="px-3 py-2 text-muted-foreground">{modelo.tipo_equipo || '—'}</td>
                              <td className="px-3 py-2">
                                <div className="flex justify-end">
                                  {!editing && (
                                    <>
                                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditModelo({ id: modelo.id, value: modelo.nombre })}>
                                        <Pencil className="h-3 w-3" />
                                      </Button>
                                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => eliminarFila('modelos_equipo', modelo.id, modelo.nombre)}>
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {modelosDelSistema.length === 0 && (
                          <tr><td colSpan={4} className="text-center py-8 text-muted-foreground text-sm">Sin modelos globales con estos filtros.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 max-h-[420px] overflow-y-auto">
                    {modelosDelSistema.map((modelo) => {
                      const marca = getMarcaById(modelo.marca_id);
                      const editing = editModelo.id === modelo.id;
                      return (
                        <div key={modelo.id} className="group p-3 rounded-md border bg-muted/30 hover:bg-muted/50">
                          <div className="flex items-center gap-1 mb-1 flex-wrap">
                            {marca && <Badge variant="outline" className="text-[10px]">{marca.nombre}</Badge>}
                            {modelo.tipo_equipo && <Badge variant="outline" className="text-[10px]">{modelo.tipo_equipo}</Badge>}
                          </div>
                          {editing ? (
                            <div className="flex items-center gap-1">
                              <Input value={editModelo.value} onChange={(e) => setEditModelo({ ...editModelo, value: e.target.value })} className="h-7 text-sm" autoFocus />
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={async () => {
                                await editarFila('modelos_equipo', modelo.id, editModelo.value);
                                setEditModelo({ id: null, value: "" });
                              }}>✓</Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditModelo({ id: null, value: "" })}>
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-sm truncate">{modelo.nombre}</span>
                              <div className="flex opacity-0 group-hover:opacity-100 shrink-0">
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditModelo({ id: modelo.id, value: modelo.nombre })}>
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => eliminarFila('modelos_equipo', modelo.id, modelo.nombre)}>
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {modelosDelSistema.length === 0 && (
                      <div className="col-span-full text-center py-8 text-muted-foreground text-sm">Sin modelos globales con estos filtros.</div>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-3 border-t items-center">
                  <select
                    value={marcaParaNuevoModelo || activeMarca || ''}
                    onChange={(e) => setMarcaParaNuevoModelo(e.target.value)}
                    className="border rounded-md px-2 py-1.5 text-sm bg-background h-9"
                  >
                    <option value="">Marca...</option>
                    {marcasDelSistema.map((m) => (
                      <option key={m.id} value={m.id}>{m.nombre}</option>
                    ))}
                  </select>
                  <Input placeholder="Nuevo modelo global..." value={nuevoModelo} onChange={(e) => setNuevoModelo(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCrearModelo()} className="max-w-xs h-9" />
                  <Button size="sm" onClick={handleCrearModelo} className="gap-1"><Plus className="h-4 w-4" /> Añadir</Button>
                  {activeTipo && <span className="text-xs text-muted-foreground">Se asociará al tipo "{activeTipoNombre}"</span>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {loading && <div className="text-center text-sm text-muted-foreground">Cargando...</div>}
    </div>
  );
}
